import { Fragment } from 'react';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef } from 'react';

import { Flex } from '@repo/ui/Flex';
import { VisuallyHidden } from '@repo/ui/VisuallyHidden';

import s from './Loader.module.css';

export const loaderVariantOptions = ['dots'] as const;
export type LoaderVariant = (typeof loaderVariantOptions)[number];

export const loaderSizeOptions = ['sm', 'md', 'lg', 'xl', 'font'] as const;
export type LoaderSize = (typeof loaderSizeOptions)[number];

export type LoaderProps = ComponentPropsWithRef<'div'> & {
  className?: string;
  label?: string;
  variant?: LoaderVariant;
  size?: LoaderSize;
};

export const loaderVariants = cva(s.loader, {
  variants: {
    variant: {
      dots: s.dotsVariant,
    },
    size: {
      xs: s.xsSize,
      sm: s.smSize,
      md: s.mdSize,
      lg: s.lgSize,
      xl: s.xlSize,
      font: s.fontSize,
    },
  },
});

const getLoaderChildren = (variant: LoaderVariant) => {
  switch (variant) {
    case 'dots':
      return (
        <Fragment>
          <div className={s.dot} />
          <div className={s.dot} />
          <div className={s.dot} />
        </Fragment>
      );
    default:
      return null;
  }
};

export const Loader = (props: LoaderProps) => {
  const {
    size = 'font',
    className,
    label = 'Loading',
    variant = 'dots',
    ...attrs
  } = props;

  const loaderChildren = getLoaderChildren(variant);

  return (
    <Flex
      alignItems="center"
      className={clsx(
        loaderVariants({
          size,
          variant,
          className,
        }),
      )}
      justifyContent="center"
      {...attrs}
    >
      {loaderChildren}
      <VisuallyHidden>{label}</VisuallyHidden>
    </Flex>
  );
};
